import { useMemo, useRef, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, ChevronLeft, ChevronRight, Trash2, TriangleAlert } from 'lucide-react';
import {
  DAILY_LIFE_COPY,
  EMPTY_STATES,
  MEAL_PHOTO_COPY,
  MEAL_SLOT_LABELS,
  MEAL_SLOT_ORDER,
  ROLES,
  type MealSlot,
} from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { Button, Card, EmptyState, ErrorNote, Spinner } from '@/components/ui';
import { apiData } from '@/lib/api';
import { useMediaUrl } from '@/lib/media-url';
import { addDays, cn, mondayOf, todayISO } from '@/lib/format';
import { uploadMedia } from '@/lib/upload';
import { useAuth } from '@/store/auth';
import type { MealWeek } from '@/types';

const WEEKDAY_LABELS = ['월', '화', '수', '목', '금', '토', '일'];

/** 식단표 — 명세서 §5.7 */
export function MealPage() {
  const membership = useAuth((s) => s.membership);
  const isParent = membership?.role === ROLES.PARENT;
  const [weekStart, setWeekStart] = useState(() => mondayOf(todayISO()));

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['meals', weekStart],
    queryFn: () => apiData<MealWeek>(`/meals?weekStart=${weekStart}`),
    placeholderData: keepPreviousData, // 주 이동 시 이전 표 유지
  });

  /** `${날짜}|${끼니}` → 메뉴. 표를 그릴 때 한 칸씩 꺼내 쓴다. */
  const byCell = useMemo(() => {
    const map = new Map<string, { menu: string[]; allergens: string[] }>();
    for (const item of data?.items ?? []) {
      map.set(`${item.mealDate}|${item.slot}`, { menu: item.menu, allergens: item.allergens });
    }
    return map;
  }, [data]);

  /** 알레르기가 걸린 칸 — 학부모 화면 하이라이트용 (§5.7) */
  const alertCells = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const alert of data?.allergyAlerts ?? []) {
      for (const hit of alert.hits) {
        const key = `${hit.mealDate}|${hit.slot}`;
        map.set(key, [...new Set([...(map.get(key) ?? []), ...hit.allergens])]);
      }
    }
    return map;
  }, [data]);

  const days = useMemo(() => weekdaysFrom(weekStart), [weekStart]);
  const shift = (weeks: number) => setWeekStart((prev) => addDays(prev, weeks * 7));
  const hasItems = (data?.items.length ?? 0) > 0;

  return (
    <Screen>
      <AppBar title="식단표" back />

      <div className="flex items-center justify-between px-4 py-3">
        <button type="button" onClick={() => shift(-1)} aria-label="지난주" className="tap p-2">
          <ChevronLeft size={20} />
        </button>
        <p className="text-base font-bold">{weekLabel(weekStart)}</p>
        <button type="button" onClick={() => shift(1)} aria-label="다음주" className="tap p-2">
          <ChevronRight size={20} />
        </button>
      </div>

      {/* 오늘의 급식 사진 (§5.7) — 학부모가 실제 배식 모습을 본다 */}
      <MealPhotosSection isStaff={!isParent} />

      {isPending && <Spinner />}
      {isError && <ErrorNote message="식단을 불러오지 못했어요." onRetry={() => void refetch()} />}

      {!isPending && !hasItems && (
        <EmptyState title={EMPTY_STATES.meal.title} body={EMPTY_STATES.meal.body} />
      )}

      {hasItems && (
        <div className="flex flex-col gap-3 p-4">
          {isParent && alertCells.size > 0 && (
            <Card className="bg-secondary-soft">
              <p className="flex items-center gap-1.5 text-sm font-semibold">
                <TriangleAlert size={15} /> {DAILY_LIFE_COPY.meal.allergyBadge}
              </p>
              <ul className="mt-1.5 flex flex-col gap-1 text-sm">
                {(data?.allergyAlerts ?? []).map((alert) => (
                  <li key={alert.childId}>
                    <span className="font-medium">{alert.childName}</span> —{' '}
                    {DAILY_LIFE_COPY.meal.allergyNotice([
                      ...new Set(alert.hits.flatMap((h) => h.allergens)),
                    ])}
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {days.map((day, index) => {
            const dayItems = MEAL_SLOT_ORDER.map((slot) => ({
              slot,
              cell: byCell.get(`${day}|${slot}`),
              allergens: alertCells.get(`${day}|${slot}`) ?? [],
            })).filter((entry) => entry.cell);

            if (dayItems.length === 0) return null;

            return (
              <Card key={day}>
                <p className="mb-2 font-semibold">
                  {WEEKDAY_LABELS[index]}요일{' '}
                  <span className="text-sm font-normal text-ink-sub">{day.slice(5)}</span>
                </p>
                <div className="flex flex-col gap-2">
                  {dayItems.map(({ slot, cell, allergens }) => (
                    <MealRow
                      key={slot}
                      slot={slot}
                      menu={cell?.menu ?? []}
                      allergens={allergens}
                      showAlert={isParent}
                    />
                  ))}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </Screen>
  );
}

interface MealPhoto {
  id: string;
  mediaId: string;
  mealType: string | null;
}

/** 오늘의 급식 사진 — 교직원이 올리고 학부모가 본다 (§5.7) */
function MealPhotosSection({ isStaff }: { isStaff: boolean }) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const today = todayISO();

  const { data } = useQuery({
    queryKey: ['meal-photos', today],
    queryFn: () => apiData<MealPhoto[]>(`/meals/photos?date=${today}`),
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiData(`/meals/photos/${id}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['meal-photos'] }),
  });

  const handleFile = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      const mediaId = await uploadMedia(file);
      await apiData('/meals/photos', { method: 'POST', body: { date: today, mediaId } });
      void queryClient.invalidateQueries({ queryKey: ['meal-photos'] });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '올리지 못했어요.');
    } finally {
      setBusy(false);
    }
  };

  const photos = data ?? [];
  // 학부모는 사진이 있을 때만 섹션이 보인다
  if (!isStaff && photos.length === 0) return null;

  return (
    <div className="px-4 pb-1">
      <Card>
        <div className="mb-2 flex items-center gap-2">
          <p className="flex items-center gap-1.5 text-sm font-semibold">
            <Camera size={15} /> {MEAL_PHOTO_COPY.title}
          </p>
          {isStaff && (
            <Button
              size="sm"
              variant="outline"
              className="ml-auto"
              loading={busy}
              onClick={() => fileRef.current?.click()}
            >
              사진 올리기
            </Button>
          )}
        </div>

        {photos.length === 0 ? (
          <p className="text-sm text-ink-sub">{MEAL_PHOTO_COPY.empty}</p>
        ) : (
          <div className="grid grid-cols-3 gap-1.5">
            {photos.map((photo) => (
              <div key={photo.id} className="relative">
                <MealPhotoImage mediaId={photo.mediaId} />
                {isStaff && (
                  <button
                    type="button"
                    aria-label="삭제"
                    className="tap absolute right-1 top-1 grid h-7 w-7 place-items-center rounded-full bg-surface/85 text-ink-sub"
                    onClick={() => remove.mutate(photo.id)}
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {error && (
          <p role="alert" className="mt-2 rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
            {error}
          </p>
        )}

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
            e.target.value = '';
          }}
        />
      </Card>
    </div>
  );
}

function MealPhotoImage({ mediaId }: { mediaId: string }) {
  const { data } = useMediaUrl(mediaId);
  if (!data) return <div className="aspect-square animate-pulse rounded-sm bg-primary-soft" />;
  return (
    <img src={data.url} alt="오늘의 급식" className="aspect-square w-full rounded-sm object-cover" />
  );
}

function MealRow({
  slot,
  menu,
  allergens,
  showAlert,
}: {
  slot: MealSlot;
  menu: string[];
  allergens: string[];
  showAlert: boolean;
}) {
  const flagged = showAlert && allergens.length > 0;

  return (
    <div
      className={cn(
        'rounded-sm px-3 py-2',
        // 알레르기 표시는 주의 환기이므로 피치를 쓴다. 발달·기록 화면이 아니라 허용된다 (§7.1).
        flagged ? 'bg-secondary-soft' : 'bg-bg',
      )}
    >
      <div className="flex items-center gap-2">
        <span className="w-16 shrink-0 text-sm text-ink-sub">{MEAL_SLOT_LABELS[slot]}</span>
        <span className="flex-1 text-base">{menu.join(', ')}</span>
      </div>
      {flagged && (
        <p className="mt-1 flex items-center gap-1 pl-16 text-sm">
          <TriangleAlert size={13} /> {allergens.join(', ')}
        </p>
      )}
    </div>
  );
}

function weekdaysFrom(weekStart: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
}

function weekLabel(weekStart: string): string {
  const end = addDays(weekStart, 6);
  return `${weekStart.slice(5).replace('-', '.')} – ${end.slice(5).replace('-', '.')}`;
}
